import { useEffect } from "react";
import { isLocale, translate, useI18n, type LocaleCode } from "../i18n/index.js";
import { useTheme, type ThemeMode } from "../theme/index.js";
import { useToast } from "../ui/index.js";
import type { Me } from "./api.js";
import { authClient } from "./auth-client.js";
import { invalidateSession } from "./session.js";

/**
 * Language and appearance are stored with the account, so they follow a person to every device.
 * A change applies at once and is saved behind it; a refused save puts the old value back.
 * The browser keeps its own copy (lang-router, ThemeProvider) only so the first paint is right.
 */

const isAppearance = (value: unknown): value is ThemeMode => value === "system" || value === "light" || value === "dark";

// Saves go out one at a time, so two quick changes land in the order they were made.
let pending: Promise<unknown> = Promise.resolve();

function save(input: { locale: LocaleCode } | { appearance: ThemeMode }): Promise<string | null> {
  const next = pending.then(async () => {
    const { error } = await authClient.updateUser(input);
    if (error) return error.message ?? translate("error.requestFailed");
    invalidateSession(); // /api/me carries the stored preferences
    return null;
  });
  pending = next.catch(() => undefined);
  return next.catch((err: unknown) => (err instanceof Error ? err.message : translate("error.requestFailed")));
}

/** Change a preference: apply now, save to the account, undo and say why if the save is refused. */
export function usePreferences({ announce = false }: { announce?: boolean } = {}) {
  const { t, locale, setLocale } = useI18n();
  const { mode, setMode } = useTheme();
  const toast = useToast();
  const settle = async (saved: Promise<string | null>, undo: () => void) => {
    const error = await saved;
    if (error) {
      undo();
      toast.danger(error);
    } else if (announce) {
      toast.success(t("settings.preferences.saved"));
    }
  };
  return {
    locale,
    appearance: mode,
    setLanguage(next: LocaleCode) {
      if (next === locale) return;
      const previous = locale;
      setLocale(next);
      void settle(save({ locale: next }), () => setLocale(previous));
    },
    setAppearance(next: ThemeMode) {
      if (next === mode) return;
      const previous = mode;
      setMode(next);
      void settle(save({ appearance: next }), () => setMode(previous));
    },
  };
}

/**
 * Adopts the preferences stored with the account whenever they differ from what the server last
 * reported — on sign-in, or after a change made on another device. A value this app cannot use
 * (a language it does not ship) is ignored rather than guessed at.
 */
export function useStoredPreferences(actor: Me["actor"] | undefined) {
  const { setLocale } = useI18n();
  const { setMode } = useTheme();
  const storedLocale = actor?.locale;
  const storedAppearance = actor?.appearance;
  useEffect(() => {
    if (isLocale(storedLocale)) setLocale(storedLocale);
  }, [storedLocale, setLocale]);
  useEffect(() => {
    if (isAppearance(storedAppearance)) setMode(storedAppearance);
    // Not keyed on setMode: it is recreated whenever the mode changes, and only a new stored value should apply.
  }, [storedAppearance]);
}
